import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $createParagraphNode,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  COMMAND_PRIORITY_NORMAL,
  createCommand,
  ElementNode,
} from 'lexical';
import { $getNearestNodeOfType } from '@lexical/utils';
import { $setBlocksType } from '@lexical/selection';
import { useEffect } from 'react';

import type { SerializedDecoratorBlockNode } from '@lexical/react/LexicalDecoratorBlockNode';
import type { EditorConfig, LexicalNode, RangeSelection } from 'lexical';

export type SerializedTableWrapperNode = SerializedDecoratorBlockNode;

export class TableWrapperNode extends ElementNode {
  static getType(): string {
    return 'tableWrapper';
  }

  static clone(node: TableWrapperNode): TableWrapperNode {
    return new TableWrapperNode(node.__key);
  }

  createDOM(config: EditorConfig): HTMLElement {
    const element = document.createElement('div');
    element.className = config.theme.tableWrapper;
    return element;
  }

  updateDOM(): false {
    return false;
  }

  insertNewAfter(_selection: RangeSelection, restoreSelection?: boolean | undefined): LexicalNode | null {
    const newBlock = $createParagraphNode();
    const direction = this.getDirection();
    newBlock.setDirection(direction);
    this.insertAfter(newBlock, restoreSelection);
    return newBlock;
  }
}

export function $createTableWrapperNode(): TableWrapperNode {
  return new TableWrapperNode();
}

export function $isTableWrapperNode(node: LexicalNode): node is TableWrapperNode {
  return node instanceof TableWrapperNode;
}

export const INSERT_TABLE_WRAPPER = createCommand('insertTableWrapperNode');

export const REMOVE_TABLE_WRAPPER = createCommand('removeTableWrapperNode');

export function TableWrapperPlugin(): null {
  const [editor] = useLexicalComposerContext();
  if (!editor.hasNodes([TableWrapperNode])) {
    throw new Error('TableWrapperPlugin: TableWrapperNode is not registered on editor');
  }
  useEffect(() => {
    const insertTableWrapper = editor.registerCommand(
      INSERT_TABLE_WRAPPER,
      () => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, $createTableWrapperNode);
        }
        return true;
      },
      COMMAND_PRIORITY_NORMAL
    );

    const removeTableWrapper = editor.registerCommand(
      REMOVE_TABLE_WRAPPER,
      () => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          const node = selection.anchor.getNode();
          if (!$isElementNode(node)) return false;
          const tableWrapperNode = $getNearestNodeOfType(node, TableWrapperNode);
          if (!tableWrapperNode) return false;
          const childrenNodes = tableWrapperNode?.getChildren();
          if (!childrenNodes) return false;
          const paragraphNode = $createParagraphNode();
          childrenNodes.forEach((child) => paragraphNode.append(child));
          tableWrapperNode.replace(paragraphNode);
        }
        return true;
      },
      COMMAND_PRIORITY_NORMAL
    );

    return () => {
      insertTableWrapper();
      removeTableWrapper();
    };
  }, [editor]);
  return null;
}
